import { useEffect, useState, useCallback } from "react";
import api, { apiErrorMessage } from "../lib/api";
import { STATUS_STYLE, fmtDate } from "../lib/schedule";
import { useShifts } from "../context/ShiftsContext";
import { Plus, Trash2, HandHelping, Inbox } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

export default function OpenShifts({ isAdmin }) {
  const { shifts: SHIFTS } = useShifts();
  const [shifts, setShifts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: "", shift: "1st", role: "CT Technologist", notes: "" });

  const load = useCallback(async () => {
    try { setShifts((await api.get("/open-shifts")).data); }
    catch (err) { toast.error(apiErrorMessage(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const post = async () => {
    if (!form.date) return toast.error("Pick a date");
    try {
      await api.post("/open-shifts", form);
      toast.success("Open shift posted");
      setOpen(false);
      setForm({ date: "", shift: "1st", role: "CT Technologist", notes: "" });
      load();
    } catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const claim = async (id) => {
    try { await api.post(`/open-shifts/${id}/claim`); toast.success("Shift picked up!"); load(); }
    catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/open-shifts/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const shiftMeta = (k) => SHIFTS.find((s) => s.key === k) || SHIFTS[0];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-display">Open Shifts</h1>
          <p className="text-sm text-slate-500 mt-1">{isAdmin ? "Post uncovered shifts for staff to pick up." : "Grab an available shift that fits you."}</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="post-open-shift-button" className="gap-2 bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4" />Post Open Shift</Button>
            </DialogTrigger>
            <DialogContent data-testid="post-open-shift-dialog">
              <DialogHeader><DialogTitle>Post an open shift</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2"><Label>Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="open-shift-date-input" /></div>
                <div className="space-y-2"><Label>Shift</Label>
                  <Select value={form.shift} onValueChange={(v) => setForm({ ...form, shift: v })}>
                    <SelectTrigger data-testid="open-shift-shift-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{SHIFTS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label} · {s.time}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-2"><Label>Role</Label>
                  <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="open-shift-role-input" /></div>
                <div className="space-y-2"><Label>Notes</Label>
                  <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Trauma coverage, etc." data-testid="open-shift-notes-input" /></div>
              </div>
              <DialogFooter><Button onClick={post} data-testid="submit-open-shift-button" className="bg-slate-900 hover:bg-slate-800">Post Shift</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {shifts.length === 0 ? (
        <EmptyState icon={Inbox} text="No open shifts right now." />
      ) : (
        <div className="space-y-6">
          {Object.entries(shifts.reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {}))
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([date, group]) => {
              const openCount = group.filter((g) => g.status === "open").length;
              return (
                <div key={date} data-testid={`open-shift-group-${date}`} className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 sm:p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      <span className="h-9 w-9 rounded-lg bg-slate-900 text-white flex items-center justify-center"><Inbox className="h-4.5 w-4.5" /></span>
                      <div>
                        <div className="text-base font-bold text-slate-900 font-display">{fmtDate(date)}</div>
                        <div className="text-xs text-slate-500">{group.length} shift{group.length === 1 ? "" : "s"} · {openCount} open</div>
                      </div>
                    </div>
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full border bg-cyan-100 text-cyan-900 border-cyan-300" data-testid={`open-shift-count-${date}`}>{group.length}</span>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {group.map((s) => {
                      const meta = shiftMeta(s.shift);
                      const timeStr = (s.start_time && s.end_time) ? `${s.start_time} – ${s.end_time}` : meta.time;
                      return (
                        <div key={s.id} data-testid={`open-shift-card-${s.id}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between gap-2">
                            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${meta.badge}`}>
                              <span className="h-2 w-2 rounded-full" style={{ background: meta.dot }} />{meta.label}
                            </div>
                            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border capitalize ${STATUS_STYLE[s.status]}`}>{s.status}</span>
                          </div>
                          <div className="mt-2 text-sm font-medium text-slate-700">{timeStr}</div>
                          <div className="mt-1 text-sm text-slate-600">{s.role}</div>
                          {s.notes && <div className="mt-1 text-xs text-slate-400">{s.notes}</div>}
                          {s.status === "filled" && <div className="mt-2 text-sm font-medium text-emerald-700">Picked up by {s.claimed_by_name}</div>}
                          <div className="mt-3 flex gap-2">
                            {!isAdmin && s.status === "open" && (
                              <Button size="sm" onClick={() => claim(s.id)} data-testid={`claim-shift-button-${s.id}`} className="gap-2 bg-cyan-600 hover:bg-cyan-700"><HandHelping className="h-4 w-4" />Pick up</Button>
                            )}
                            {isAdmin && (
                              <Button size="sm" variant="outline" onClick={() => remove(s.id)} data-testid={`delete-shift-button-${s.id}`} className="gap-2 text-rose-600 hover:text-rose-700"><Trash2 className="h-4 w-4" />Remove</Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}

export function EmptyState({ icon: Icon, text }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white/50 py-16 flex flex-col items-center justify-center text-slate-400">
      <Icon className="h-10 w-10 mb-3" strokeWidth={1.5} />
      <p className="text-sm font-medium">{text}</p>
    </div>
  );
}
